import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch, buildApiUrl, fetchWithAuth } from '../utils/apiClient';
import { hasLogoutFlag, clearLogoutFlag, setLogoutFlag } from '../utils/sessionUtils';
import { setTabSessionToken, clearTabSessionToken, hasTabSession } from '../utils/tabSession';
import { setTokens, clearTokens } from '../utils/tokenStore';
import { getCharacterWorkspaceId } from '../utils/identity';

export interface AuthUser {
  uuid?: string;
  id?: string | number;
  name?: string;
  first_name?: string;
  last_name?: string;
  title?: string;
  preferred_name?: string;
  displayName?: string;
  email?: string;
  photo_url?: string;
  role?: string;
  roles?: string[];
  permissions?: string[];
  character_type?: string;
  character?: string;
  demo_mode?: boolean;
  is_demo?: boolean;
  is_gated_demo?: boolean;
  demo_expires_at?: string;
  is_active?: boolean;
  impersonates_user_id?: string;
  organization_id?: string;
  organization_name?: string;
  practitioner_id?: string;
  supervisor_id?: string;
  requires_supervision?: boolean;
  can_prescribe?: boolean;
  accepting_new_clients?: boolean;
  accepting_new_client_online_self_bookings?: boolean;
  license_type?: string;
  license_number?: string;
  license_state?: string;
  npi_number?: string;
  specialties?: string[];
  degrees?: string[];
  bio?: string;
  website_url?: string;
  subscription_status?: string;
  has_admin_settings_access?: boolean;
  timesheet_mode?: string;
  claimed_org?: boolean;
  clinic_name?: string;
  demo_character_ids?: string[];
  has_active_subscription?: boolean;
  can_manage_subscription?: boolean;
  meta_data?: Record<string, unknown>;
  preferences?: Record<string, unknown>;
  oauth_user?: boolean;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  signup_consent_completed?: boolean;
  [key: string]: unknown;
}

export interface AuthContextValue {
  user: AuthUser | false | null;
  loading: boolean;
  ready: boolean;
  error: string | null;
  errorStatus: number | null;
  logout: (redirectPath?: string) => Promise<void>;
  refreshAuth: () => Promise<AuthUser | false | null>;
  checkAuth: () => Promise<AuthUser | false>;
  invalidateAuth: (reason?: string) => Promise<AuthUser | false>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export { AuthContext };

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

let authCache: { user: AuthUser | false | null; at: number } = { user: null, at: 0 };
let inflightAuthPromise: Promise<{ ok: boolean; status: number; data: unknown }> | null = null;
const FRESH_WINDOW_MS = 20000;
const RATE_LIMIT_RETRY_MS = 1500;
let lastStatus: number | null = null;

export function getPreferredLoginPath(): string {
  try {
    return localStorage.getItem('preferred_login_path') || '/login';
  } catch {
    return '/login';
  }
}

/** Throttling (429), server errors (5xx) and network failures (0) do not prove
 *  the session ended, so an already-authenticated user must survive them. */
export function isTransientAuthFailure(status: number): boolean {
  return status === 429 || status === 0 || status >= 500;
}

/** Returns the cached authenticated user when a failure must not sign them out. */
function cachedUserForTransientFailure(status: number): AuthUser | null {
  if (!isTransientAuthFailure(status)) return null;
  const cached = authCache.user;
  return cached ? cached : null;
}

/**
 * Best-effort client-side cookie clearing.
 * HttpOnly cookies (base_session, role cookies) cannot be removed by JS,
 * but this catches any non-HttpOnly cookies and prevents stale state.
 */
function clearBrowserCookies(): void {
  try {
    const cookieNames = [
      'base_session', 'session', 'client_session', 'demo_session',
      'demo_session_id', 'provider_session', 'therapist_session', 'admin_session',
    ];
    for (const name of cookieNames) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
    }
  } catch (_) { /* ignore */ }
}


export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const isDevMode = import.meta.env.VITE_DEV_MODE === "true";

  const normalizeUser = (data: unknown): AuthUser | null => {
    const rawUser = (data && typeof data === 'object' && (data as Record<string, unknown>).user) ? (data as Record<string, unknown>).user : data;
    if (!rawUser || typeof rawUser !== 'object') return null;
    const raw = rawUser as Record<string, unknown>;
    return { ...raw, character_type: (raw.character_type || raw.character) as string | undefined } as AuthUser;
  };

  /**
   * Attempt to claim a tab-scoped session token from the backend.
   * Only succeeds when the server has a pending login (create_session_on_login flag).
   * The token is stored in sessionStorage so it's tab-scoped.
   */
  const claimTabSession = async (): Promise<boolean> => {
    try {
      const res = await apiFetch(buildApiUrl('/api/auth/tab-session'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const body = await res.json();
        if (body.tab_session_token) {
          setTabSessionToken(body.tab_session_token);
          return true;
        }
      }
    } catch {
      // claim failed — new tab with no pending login
    }
    return false;
  };

  const fetchMe = async () => {
    // If we don't have a tab session token yet, try to claim one first.
    // This handles: (a) first load after login, (b) OAuth redirect landing.
    // If the claim fails (no pending login), the tab is not authenticated.
    if (!hasTabSession()) {
      const claimed = await claimTabSession();
      if (!claimed) {
        // No pending login to claim — this tab must authenticate independently
        return { ok: false, status: 401, data: null };
      }
    }

    let res = await fetchWithAuth(buildApiUrl('/api/auth/me'), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }, false);
    // A throttled session probe says nothing about whether the session is
    // valid, so retry once before the caller has to interpret the failure.
    if (res.status === 429) {
      await new Promise((resolve) => setTimeout(resolve, RATE_LIMIT_RETRY_MS));
      res = await fetchWithAuth(buildApiUrl('/api/auth/me'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }, false);
    }
    if (!res.ok) {
      // If tab session expired on the server side, clear the stale token
      if (res.status === 401) {
        clearTabSessionToken();
      }
      return { ok: false, status: res.status, data: null };
    }
    const data = await res.json();
    return { ok: true, status: res.status, data };
  };

  const checkAuth = useCallback(async (): Promise<AuthUser | false> => {
    try {
      setError(null);
      const now = Date.now();

      if (authCache.user && now - authCache.at < FRESH_WINDOW_MS) {
        setUser(authCache.user);
        setLoading(false);
        return authCache.user;
      }

      if (!inflightAuthPromise) {
        inflightAuthPromise = (async () => {
          const res = await fetchMe();
          return res;
        })();
      }

      const res = await inflightAuthPromise.finally(() => {
        inflightAuthPromise = null;
      });

      if (!res.ok) {
        lastStatus = res.status;
        setErrorStatus(res.status);
        if (hasLogoutFlag()) {
          clearLogoutFlag();
        }
        // Throttling and server/network failures say nothing about whether the
        // session is valid, so keep an already-authenticated user signed in and
        // leave the cache untouched so the next check re-probes, rather than
        // bouncing them to the login page.
        const transientUser = cachedUserForTransientFailure(res.status);
        if (transientUser) {
          setUser(transientUser);
          return transientUser;
        }
        setUser(false);
        authCache = { user: false, at: Date.now() };
        return false;
      }

      const normalized = normalizeUser(res.data);
      if (normalized && Object.keys(normalized).length > 0 && (normalized.uuid || normalized.demo_mode)) {
        lastStatus = 200;
        setErrorStatus(null);
        if (normalized.uuid && !normalized.demo_mode) {
          const prevUuid = sessionStorage.getItem('user_uuid');
          sessionStorage.setItem('user_uuid', normalized.uuid);
          if (prevUuid && prevUuid !== normalized.uuid) {
            window.dispatchEvent(new CustomEvent('user-uuid-changed', { detail: { uuid: normalized.uuid } }));
          }
        }
        setUser(normalized);
        authCache = { user: normalized, at: Date.now() };
        return normalized;
      }
      lastStatus = 401;
      setErrorStatus(401);
      setUser(false);
      authCache = { user: false, at: Date.now() };
      return false;
    } catch (err) {
      lastStatus = 0;
      setErrorStatus(0);
      setError((err as Error)?.message || 'Auth error');
      const transientUser = cachedUserForTransientFailure(0);
      if (transientUser) {
        setUser(transientUser);
        return transientUser;
      }
      setUser(false);
      authCache = { user: false, at: Date.now() };
      return false;
    } finally {
      setLoading(false);
      setReady(true);
    }
  }, []);

  const invalidateAuth = useCallback((reason = 'manual') => {
    authCache = { user: null, at: 0 };
    inflightAuthPromise = null;
    lastStatus = null;
    setError(null);
    setErrorStatus(null);
    setLoading(true);
    // For session-changing transitions (login, demo switch, exit) the user
    // identity changes, so reset ready and user so route guards show
    // "Checking login..." instead of rendering with stale data.
    // Non-session-changing callers (refresh, profile_update, etc.) keep the
    // current user/ready so auth-gated fetches aren't unnecessarily disrupted.
    const SESSION_CHANGE_REASONS = ['login', 'demo-login', 'exit-demo', 'switch-character'];
    if (SESSION_CHANGE_REASONS.includes(reason)) {
      // Clear the old tab session so a new one is claimed from the
      // fresh cookie session that the login/switch endpoint just set.
      clearTabSessionToken();
      setReady(false);
      setUser(null);
    }
    try { localStorage.setItem('auth:invalidate', JSON.stringify({ t: Date.now(), reason })); } catch (_) { /* ignore */ }
    // Return the checkAuth() promise so callers (e.g. refreshAuth) can
    // await the /api/auth/me round-trip and see the fresh user state
    // before continuing (e.g. navigating to a page that depends on it).
    return checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async (redirectPath?: string) => {
    let targetPath = redirectPath;
    if (!targetPath) {
      targetPath = getPreferredLoginPath();
    }
    const performLogoutCleanup = () => {
      const hadInflightBooking = (() => { try { return localStorage.getItem('booking:inflight') === '1'; } catch { return false; } })();
      const inflightLeadId = (() => { try { return localStorage.getItem('booking:lead') || ''; } catch { return ''; } })();
      const tabStorageKey = getCharacterWorkspaceId(user);
      const preferredLoginPath = (() => {
        try { return localStorage.getItem('preferred_login_path'); } catch { return null; }
      })();
      try {
        const savedTabSession = tabStorageKey
          ? localStorage.getItem(`kindredly_tabs_${tabStorageKey}`)
          : null;
        clearTokens();
        clearBrowserCookies();
        localStorage.clear();
        if (tabStorageKey && savedTabSession) {
          localStorage.setItem(`kindredly_tabs_${tabStorageKey}`, savedTabSession);
        }
        if (preferredLoginPath) {
          localStorage.setItem('preferred_login_path', preferredLoginPath);
        }
      } catch { /* ignore storage errors */ }
      sessionStorage.clear();
      if (hadInflightBooking) {
        try { localStorage.setItem('booking:abandoned', '1'); } catch (_) { /* ignore */ }
      }
      if (inflightLeadId) {
        try { localStorage.setItem('booking:lead', inflightLeadId); } catch (_) { /* ignore */ }
      }
      setUser(false);
      authCache = { user: false, at: Date.now() };
      setError(null);
      setErrorStatus(null);
      try { localStorage.setItem('auth:invalidate', JSON.stringify({ t: Date.now(), reason: 'logout' })); } catch (_) { /* ignore */ }
    };

    try {
      setLogoutFlag();
      // Log out first, while the tab session is still valid.  Once a session
      // is tab-bound, RoleSessionIsolationMiddleware rejects requests whose
      // X-Tab-Session no longer resolves, so deleting the tab session first
      // makes /api/auth/logout unreachable and leaves the server session
      // alive — poisoning every later login attempt in this browser.
      await apiFetch(buildApiUrl('/api/auth/logout'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      // Then drop the tab session from Redis (uses the X-Tab-Session header
      // injected by apiClient) so it is gone even if the logout call failed.
      await apiFetch(buildApiUrl('/api/auth/tab-session'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }).catch(() => {});
    } catch {
      // Proceed with local cleanup even if server logout fails
    }
    clearTabSessionToken();
    performLogoutCleanup();
    window.location.href = targetPath || '/login';
  }, [user]);

  const refresh = useCallback(async () => {
    try {
      const resp = await apiFetch(buildApiUrl('/api/auth/refresh'), { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (resp.ok) {
        const body = await resp.json().catch(() => ({}));
        if (body && body.access_token) {
          setTokens(body.access_token, body.token_type);
        }
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      await refresh();
      // Await the full /api/auth/me re-fetch so callers can rely on the
      // updated user state (e.g. claimed_org: true after ClaimOrgDialog)
      // immediately after `await refreshAuth()` resolves.
      await invalidateAuth('refresh');
      // Use ?? so an authenticated-fail (authCache.user === false) is returned
      // as-is instead of falling through to the stale hook `user` variable.
      return authCache.user ?? user;
    } catch {
      setUser(false);
      authCache = { user: false, at: Date.now() };
      return null;
    }
  }, [refresh, invalidateAuth, user]);
  // Listen for global auth:session-expired events dispatched by fetchWithAuth
  // when a 401 is received and cannot be recovered.  Triggers a full logout
  // + redirect so the user doesn't sit on a broken page seeing 500 toasts.
  useEffect(() => {
    const handleSessionExpired = () => {
      logout();
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [logout]);

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'auth:invalidate' && e.newValue) {
        // Debounce cross-tab invalidation to prevent burst of /me requests
        // when multiple tabs react to a single localStorage write.
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          authCache = { user: null, at: 0 };
          setLoading(true);
          setReady(false);
          checkAuth();
        }, 500);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [checkAuth]);


  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value = { user, loading, ready, error, errorStatus, logout, refreshAuth, checkAuth, invalidateAuth };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
